import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import SyncIcon from "@mui/icons-material/Sync";
import TopNavBar from "../components/TopNavBar";
import { useNavigate, useParams } from "react-router-dom";
import {
  getRouteTemplateById,
  getAvailableRooms,
  replaceRouteRooms,
  createRoute,
  getMasterRouteRooms,
  reorderMasterRouteRooms,
  updateRoute,
  updateRouteRoom,
  getRoutePeriodConsistency,
} from "../api/routeTemplates";
import RoutePeriodSyncDialog from "../components/routeTemplates/RoutePeriodSyncDialog";
import { getRoomRestrictionColor } from "../utils/roomRestriction";

// Edits on this page are saved immediately but synced to jobs and assignment
// periods only when the user runs "Sync" (from a date they choose). Each
// edit is sent with defer_sync so the backend does not sync after every call.
const DEFER_SYNC = { deferSync: true };

// Remember an unsynced edit across reloads (per browser) so the "sync needed"
// notice is not lost if the page is closed before syncing.
function pendingSyncKey(templateId) {
  return `routeTemplate:${templateId}:pendingSync`;
}
function readPendingSync(templateId) {
  try {
    return window.localStorage.getItem(pendingSyncKey(templateId)) === "1";
  } catch {
    return false;
  }
}
function writePendingSync(templateId, pending) {
  try {
    if (pending) {
      window.localStorage.setItem(pendingSyncKey(templateId), "1");
    } else {
      window.localStorage.removeItem(pendingSyncKey(templateId));
    }
  } catch {
    // Storage unavailable (private mode etc.): the notice just won't survive a reload.
  }
}

const MANUFACTURER_COLORS = {
  bosch: "#1565c0",
  samsung: "#e65100",
  lionsbot: "#2e7d32",
};

function manufacturerColor(mfr) {
  return MANUFACTURER_COLORS[mfr?.toLowerCase()] || "#546e7a";
}

function sortRoomsByExecutionOrder(rooms = []) {
  return [...rooms].sort((a, b) => {
    const aOrder = Number(a.execution_order || a.executionOrder || 0);
    const bOrder = Number(b.execution_order || b.executionOrder || 0);
    if (aOrder !== bOrder) return aOrder - bOrder;
    return Number(a.pk_id || 0) - Number(b.pk_id || 0);
  });
}

function roomHierarchyLabel(room) {
  return {
    location: room.location_name || "Unspecified location",
    building: room.building_name || "Unspecified building",
    department: room.department_name || "Unspecified department",
  };
}

function sortUnassignedRoomsByHierarchy(rooms = []) {
  return [...rooms].sort((a, b) => {
    const aHierarchy = roomHierarchyLabel(a);
    const bHierarchy = roomHierarchyLabel(b);

    const locationCompare = aHierarchy.location.localeCompare(
      bHierarchy.location,
    );
    if (locationCompare !== 0) return locationCompare;

    const buildingCompare = aHierarchy.building.localeCompare(
      bHierarchy.building,
    );
    if (buildingCompare !== 0) return buildingCompare;

    const departmentCompare = aHierarchy.department.localeCompare(
      bHierarchy.department,
    );
    if (departmentCompare !== 0) return departmentCompare;

    return String(a.room_name || a.room_code_snapshot || "").localeCompare(
      String(b.room_name || b.room_code_snapshot || ""),
    );
  });
}

function groupRoomsByHierarchy(rooms = []) {
  const grouped = [];
  const locationMap = new Map();

  rooms.forEach((room) => {
    const hierarchy = roomHierarchyLabel(room);
    const locationKey = hierarchy.location;
    const buildingKey = `${locationKey}||${hierarchy.building}`;
    const departmentKey = `${buildingKey}||${hierarchy.department}`;

    if (!locationMap.has(locationKey)) {
      const locationGroup = {
        key: locationKey,
        label: hierarchy.location,
        buildings: [],
        buildingMap: new Map(),
      };
      locationMap.set(locationKey, locationGroup);
      grouped.push(locationGroup);
    }

    const locationGroup = locationMap.get(locationKey);

    if (!locationGroup.buildingMap.has(buildingKey)) {
      const buildingGroup = {
        key: buildingKey,
        label: hierarchy.building,
        departments: [],
        departmentMap: new Map(),
      };
      locationGroup.buildingMap.set(buildingKey, buildingGroup);
      locationGroup.buildings.push(buildingGroup);
    }

    const buildingGroup = locationGroup.buildingMap.get(buildingKey);

    if (!buildingGroup.departmentMap.has(departmentKey)) {
      const departmentGroup = {
        key: departmentKey,
        label: hierarchy.department,
        rooms: [],
      };
      buildingGroup.departmentMap.set(departmentKey, departmentGroup);
      buildingGroup.departments.push(departmentGroup);
    }

    buildingGroup.departmentMap.get(departmentKey).rooms.push(room);
  });

  return grouped;
}

function buildRoomSections(rooms = [], isUnassigned = false) {
  if (rooms.length === 0) {
    return [];
  }

  if (isUnassigned) {
    return groupRoomsByHierarchy(rooms).flatMap((locationGroup) =>
      locationGroup.buildings.flatMap((buildingGroup) =>
        buildingGroup.departments.map((departmentGroup) => ({
          key: departmentGroup.key,
          locationLabel: locationGroup.label,
          buildingLabel: buildingGroup.label,
          departmentLabel: departmentGroup.label,
          rooms: departmentGroup.rooms,
        })),
      ),
    );
  }

  const sections = [];
  let currentSection = null;

  rooms.forEach((room) => {
    const hierarchy = roomHierarchyLabel(room);
    const sectionKey = `${hierarchy.location}||${hierarchy.building}||${hierarchy.department}`;

    if (!currentSection || currentSection.compareKey !== sectionKey) {
      currentSection = {
        key: `${sectionKey}||${sections.length}`,
        compareKey: sectionKey,
        locationLabel: hierarchy.location,
        buildingLabel: hierarchy.building,
        departmentLabel: hierarchy.department,
        rooms: [],
      };
      sections.push(currentSection);
    }

    currentSection.rooms.push(room);
  });

  return sections;
}

// ─── Room Chip ────────────────────────────────────────────────────────────────
function RoomChip({
  room,
  isSelected,
  onSelect,
  roomIndex,
  orderedRooms,
  dimmed,
  isDragging = false,
  draggable = false,
  onDragStart,
  onDragEnd,
  canToggle = false,
  toggling = false,
  onToggleActive,
}) {
  const mfrColor = manufacturerColor(room.manufacturer);
  const isActive = room.active !== 0 && room.active !== false;
  return (
    <Box
      data-room-chip="true"
      onClick={(event) => onSelect(room, event, roomIndex, orderedRooms)}
      draggable={draggable}
      onDragStart={(event) =>
        onDragStart?.(event, room, roomIndex, orderedRooms)
      }
      onDragEnd={() => onDragEnd?.()}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        px: 1.25,
        py: 0.6,
        borderRadius: 1.5,
        border: "1px solid",
        borderColor: isSelected ? "primary.main" : "divider",
        bgcolor: isSelected ? "primary.50" : dimmed ? "#f5f5f5" : "#fff",
        cursor: draggable ? "grab" : "pointer",
        opacity: isDragging ? 0.35 : dimmed ? 0.55 : 1,
        transform: isDragging ? "scale(0.99)" : "scale(1)",
        boxShadow: isDragging
          ? "0 8px 18px rgba(15, 23, 42, 0.18)"
          : isSelected
            ? "0 0 0 2px rgba(25,118,210,0.35)"
            : "none",
        transition:
          "box-shadow 180ms ease, border-color 180ms ease, transform 180ms ease, opacity 180ms ease, background-color 180ms ease",
        "&:hover": {
          borderColor: isSelected ? "primary.main" : "primary.light",
        },
      }}
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          bgcolor: room.manufacturer ? mfrColor : "#9e9e9e",
          flexShrink: 0,
        }}
      />
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          lineHeight: 1.2,
          color: getRoomRestrictionColor(room.room_restriction),
        }}
      >
        {roomIndex + 1}.{room.room_code_snapshot || room.room_name}
      </Typography>
      {room.manufacturer && (
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", ml: 0.25 }}
        >
          {room.device_model ? ` ${room.device_model}` : ""}
        </Typography>
      )}
      {canToggle && (
        <Box
          sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.25 }}
          onClick={(event) => event.stopPropagation()}
        >
          {toggling && <CircularProgress size={14} />}
          <Switch
            size="small"
            checked={isActive}
            disabled={toggling}
            onChange={() => onToggleActive?.(room)}
          />
        </Box>
      )}
    </Box>
  );
}

// ─── Route Column ─────────────────────────────────────────────────────────────
function RouteColumn({
  route,
  isRoomSelected,
  onSelectRoom,
  isUnassigned,
  onToggleRouteActive,
  onToggleRoomActive,
  dragState,
  onDragStartRoom,
  onDragEndRoom,
  onDragOverColumn,
  onDropOnColumn,
  routeSaving,
  roomSavingById,
}) {
  const rooms = route.rooms || [];
  const orderedRooms = isUnassigned
    ? sortUnassignedRoomsByHierarchy(rooms)
    : sortRoomsByExecutionOrder(rooms);
  const roomSections = buildRoomSections(orderedRooms, isUnassigned);
  const columnIsDropTarget =
    dragState?.active && String(dragState.overRouteId) === String(route.pk_id);

  const headerBg =
    route.active === 0 || route.active === false ? "#f5f5f5" : "#f0f7ff";
  const headerBorder =
    route.active === 0 || route.active === false ? "#e0e0e0" : "#bbdefb";

  return (
    <Paper
      elevation={0}
      sx={{
        minWidth: 220,
        maxWidth: 260,
        flexShrink: 0,
        borderRadius: 2,
        border: "1px solid",
        borderColor: headerBorder,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Column header */}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          bgcolor: headerBg,
          borderBottom: "1px solid",
          borderColor: headerBorder,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {isUnassigned ? "Ikke tildelt" : route.route_name || route.route_code}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Chip
            label={rooms.length}
            size="small"
            sx={{ height: 20, fontSize: "0.7rem" }}
          />
          {!isUnassigned && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
              {routeSaving && <CircularProgress size={14} />}
              <Switch
                size="small"
                checked={route.active !== 0 && route.active !== false}
                disabled={routeSaving}
                onChange={() => onToggleRouteActive?.(route)}
              />
            </Box>
          )}
          {!isUnassigned && (route.active === 0 || route.active === false) && (
            <Chip
              label="Inactive"
              size="small"
              color="default"
              variant="outlined"
              sx={{ height: 20, fontSize: "0.7rem" }}
            />
          )}
        </Box>
      </Box>

      {/* Rooms */}
      <Box
        sx={{
          p: 1,
          display: "flex",
          flexDirection: "column",
          gap: 0.75,
          flex: 1,
          transition: "background-color 180ms ease",
          bgcolor: columnIsDropTarget
            ? "rgba(25, 118, 210, 0.04)"
            : "transparent",
        }}
        onDragOver={(event) => {
          event.stopPropagation();
          onDragOverColumn?.(event, route.pk_id, orderedRooms.length);
        }}
        onDrop={(event) => {
          event.stopPropagation();
          onDropOnColumn?.(event, route.pk_id, orderedRooms.length);
        }}
      >
        {roomSections.map((roomSection) => (
          <Box key={roomSection.key}>
            <Box
              sx={{
                px: 0.5,
                pt: isUnassigned ? 0.5 : 0.75,
                pb: 0.25,
                borderTop: isUnassigned ? "none" : "1px dashed",
                borderColor: isUnassigned ? "transparent" : "divider",
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, display: "block" }}
              >
                {roomSection.locationLabel}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block" }}
              >
                {roomSection.buildingLabel}
              </Typography>
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ display: "block", mb: 0.5 }}
              >
                {roomSection.departmentLabel}
              </Typography>
            </Box>

            {roomSection.rooms.map((room) => {
              const index = orderedRooms.findIndex(
                (orderedRoom) =>
                  String(orderedRoom.room_id || orderedRoom.pk_id) ===
                    String(room.room_id || room.pk_id) &&
                  String(orderedRoom.pk_id || "") === String(room.pk_id || ""),
              );

              return (
                <Box
                  key={`${roomSection.key}-${room.pk_id || room.room_id}`}
                  onDragOver={(event) => {
                    event.stopPropagation();
                    onDragOverColumn?.(event, route.pk_id, index);
                  }}
                  onDrop={(event) => {
                    event.stopPropagation();
                    onDropOnColumn?.(event, route.pk_id, index);
                  }}
                  sx={{
                    position: "relative",
                    pt:
                      columnIsDropTarget &&
                      Number(dragState?.overIndex) === index
                        ? 0.9
                        : 0,
                    transition: "padding-top 160ms ease",
                    "&::before":
                      columnIsDropTarget &&
                      Number(dragState?.overIndex) === index
                        ? {
                            content: '""',
                            position: "absolute",
                            left: 8,
                            right: 8,
                            top: 2,
                            height: 3,
                            borderRadius: 4,
                            bgcolor: "primary.main",
                            opacity: 0.85,
                          }
                        : {},
                  }}
                >
                  <RoomChip
                    room={room}
                    roomIndex={index}
                    orderedRooms={orderedRooms}
                    isSelected={isRoomSelected(room)}
                    onSelect={onSelectRoom}
                    dimmed={room.active === 0 || room.active === false}
                    isDragging={
                      dragState?.active &&
                      dragState.dragSelectionKeys?.includes(
                        `${route.pk_id}:${room.pk_id || "na"}:${room.room_id || room.pk_id}`,
                      )
                    }
                    draggable
                    onDragStart={(
                      event,
                      draggedRoom,
                      draggedRoomIndex,
                      draggedOrderedRooms,
                    ) =>
                      onDragStartRoom?.(
                        event,
                        draggedRoom,
                        route.pk_id,
                        draggedRoomIndex,
                        draggedOrderedRooms,
                      )
                    }
                    onDragEnd={onDragEndRoom}
                    canToggle={!isUnassigned && Boolean(room.pk_id)}
                    toggling={Boolean(roomSavingById?.[room.pk_id])}
                    onToggleActive={() =>
                      onToggleRoomActive?.(route.pk_id, room)
                    }
                  />
                </Box>
              );
            })}
          </Box>
        ))}
        {rooms.length === 0 && (
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ px: 0.5, py: 1 }}
          >
            {isUnassigned ? "No unassigned rooms" : "No assigned rooms"}
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

// ─── Add Route Dialog ─────────────────────────────────────────────────────────
function AddRouteDialog({ open, onClose, onSubmit, saving }) {
  const [routeName, setRouteName] = useState("");

  const toRouteCode = (name) =>
    String(name || "")
      .trim()
      .replace(/\s+/g, "_")
      .toUpperCase();

  const handleSubmit = () => {
    if (!routeName.trim()) return;
    const normalizedName = routeName.trim();
    onSubmit({
      routeCode: toRouteCode(normalizedName),
      routeName: normalizedName,
    });
  };

  // Reset on open
  useEffect(() => {
    if (open) {
      setRouteName("");
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Create New Route</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField
            label="Route Name"
            value={routeName}
            onChange={(e) => setRouteName(e.target.value)}
            size="small"
            fullWidth
            placeholder="e.g. Route 4"
          />
          <TextField
            label="Route Code (auto)"
            value={toRouteCode(routeName)}
            size="small"
            fullWidth
            disabled
            helperText="Generated automatically from route name"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || !routeName.trim()}
        >
          {saving ? <CircularProgress size={18} /> : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Master Order Dialog ──────────────────────────────────────────────────────
// Lets a user merge routes into one global execution order (global_order),
// independent of each room's per-route execution_order.
function MasterOrderDialog({ open, templateId, onClose }) {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);

  useEffect(() => {
    if (!open || !templateId) return;
    setLoading(true);
    setError(null);
    getMasterRouteRooms(templateId)
      .then((data) => setRooms(data.rooms || []))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [open, templateId]);

  const handleDragOver = (event, index) => {
    event.preventDefault();
    if (dragIndex === null || dragIndex === index) return;
    setRooms((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(index, 0, moved);
      return next;
    });
    setDragIndex(index);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await reorderMasterRouteRooms(
        templateId,
        rooms.map((room) => room.pk_id),
      );
      onClose(true);
    } catch (err) {
      setError(err.message || "Failed to save master order");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => (!saving ? onClose(false) : null)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Master rækkefølge</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Træk rummene for at fastlægge den samlede rækkefølge på tværs af
          ruter. Bruges når to eller flere ruter skal slås sammen til én
          udførelsesrækkefølge.
        </Typography>

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 1.5 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Stack spacing={0.75}>
            {rooms.map((room, index) => (
              <Box
                key={room.pk_id}
                draggable
                onDragStart={() => setDragIndex(index)}
                onDragOver={(event) => handleDragOver(event, index)}
                onDrop={(event) => event.preventDefault()}
                onDragEnd={() => setDragIndex(null)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.25,
                  py: 0.75,
                  borderRadius: 1.5,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: dragIndex === index ? "primary.50" : "#fff",
                  cursor: "grab",
                  opacity: dragIndex === index ? 0.6 : 1,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, width: 22, flexShrink: 0 }}
                >
                  {index + 1}
                </Typography>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: room.manufacturer
                      ? manufacturerColor(room.manufacturer)
                      : "#9e9e9e",
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {room.room_code_snapshot || room.room_name}
                </Typography>
                <Chip
                  label={room.route_name || room.route_code || "Ikke tildelt"}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: "0.7rem" }}
                />
              </Box>
            ))}
            {rooms.length === 0 && (
              <Typography variant="body2" color="text.disabled">
                Ingen rum i denne skabelon endnu.
              </Typography>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)} disabled={saving}>
          Annuller
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || loading || rooms.length === 0}
        >
          {saving ? <CircularProgress size={18} /> : "Gem rækkefølge"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ConfigureRouteTemplatePage() {
  const { templateId } = useParams();
  const navigate = useNavigate();

  const [template, setTemplate] = useState(null);
  const [unassignedRooms, setUnassignedRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Selection & bulk action state
  const [selectedRooms, setSelectedRooms] = useState([]); // [{ ...room, room_id, fromRouteId, selectionKey }]
  const [selectionAnchorByColumn, setSelectionAnchorByColumn] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(null);
  const [routeStatusSavingById, setRouteStatusSavingById] = useState({});
  const [roomStatusSavingById, setRoomStatusSavingById] = useState({});
  const [needsSync, setNeedsSyncState] = useState(() =>
    readPendingSync(templateId),
  );
  // null | "sync" | "rebuild"
  const [syncDialogMode, setSyncDialogMode] = useState(null);
  // Read-only check: do current assignment periods (from today) still match
  // the routes? Surfaces drift the daily automation run only logs.
  const [periodConsistency, setPeriodConsistency] = useState(null);
  const [dragState, setDragState] = useState({
    active: false,
    sourceRouteId: null,
    overRouteId: null,
    overIndex: null,
    dragSelectionKeys: [],
    dragSelectionEntries: [],
  });

  // Add route dialog
  const [addRouteOpen, setAddRouteOpen] = useState(false);
  const [addRouteSaving, setAddRouteSaving] = useState(false);

  // Master order dialog
  const [masterOrderOpen, setMasterOrderOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      getRouteTemplateById(templateId),
      getAvailableRooms(templateId),
    ])
      .then(([tplData, roomsData]) => {
        setTemplate(tplData.template);
        // Unassigned = rooms where is_assigned === 0
        const unassigned = (roomsData.rooms || [])
          .filter((r) => r.is_assigned === 0)
          .map((r) => ({
            pk_id: r.pk_id,
            room_id: r.pk_id,
            room_code_snapshot: r.room_name,
            room_name: r.room_name,
            room_restriction: r.room_restriction,
            department_pk_id: r.department_pk_id,
            department_name: r.department_name,
            building_pk_id: r.building_pk_id,
            building_name: r.building_name,
            location_pk_id: r.location_pk_id,
            location_name: r.location_name,
            device_model: r.device_model || null,
            active: 1,
          }));
        setUnassignedRooms(unassigned);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [templateId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (selectedRooms.length === 0) return;

    const handleDocumentMouseDown = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const clickedRoomChip = target.closest('[data-room-chip="true"]');
      const clickedSelectionActions = target.closest(
        '[data-selection-actions="true"]',
      );

      if (!clickedRoomChip && !clickedSelectionActions) {
        setSelectedRooms([]);
        setSelectionAnchorByColumn({});
      }
    };

    document.addEventListener("mousedown", handleDocumentMouseDown);

    return () => {
      document.removeEventListener("mousedown", handleDocumentMouseDown);
    };
  }, [selectedRooms.length]);

  const getSelectionKey = (room, fromRouteId) => {
    const roomId = room.room_id || room.pk_id;
    const roomMappingId = room.pk_id || "na";
    return `${fromRouteId}:${roomMappingId}:${roomId}`;
  };

  const isRoomSelected = (room, fromRouteId) => {
    const key = getSelectionKey(room, fromRouteId);
    return selectedRooms.some((r) => r.selectionKey === key);
  };

  const toSelectionEntry = (room, fromRouteId) => {
    const id = room.room_id || room.pk_id;
    const selectionKey = getSelectionKey(room, fromRouteId);
    return { ...room, room_id: id, fromRouteId, selectionKey };
  };

  const normalizeRoomsForApi = (rooms) =>
    rooms.map((r, index) => ({
      roomId: r.room_id || r.pk_id || r.roomId,
      executionOrder: index + 1,
      roomCodeSnapshot:
        r.room_code_snapshot || r.room_name || r.roomCodeSnapshot || null,
      manufacturer: r.manufacturer || null,
      deviceModel: r.device_model || r.deviceModel || null,
      // Keep the room's master position (global_order); the backend also
      // preserves it when this is missing.
      globalOrder: r.global_order ?? r.globalOrder ?? null,
      active: r.active !== undefined ? r.active : 1,
    }));

  const setNeedsSync = useCallback(
    (pending) => {
      setNeedsSyncState(pending);
      writePendingSync(templateId, pending);
    },
    [templateId],
  );

  const markSyncNeeded = () => {
    setNeedsSync(true);
    setSaveSuccess(null);
  };

  const loadPeriodConsistency = useCallback(() => {
    if (!templateId) return;
    getRoutePeriodConsistency(templateId, { lookaheadDays: 14 })
      .then((data) => setPeriodConsistency(data?.result || null))
      // Informational only; ignore if the automation service is unreachable.
      .catch(() => setPeriodConsistency(null));
  }, [templateId]);

  useEffect(() => {
    loadPeriodConsistency();
  }, [loadPeriodConsistency]);

  const handleSyncDialogClose = ({ completed, message } = {}) => {
    const mode = syncDialogMode;
    setSyncDialogMode(null);
    if (message) setSaveSuccess(message);
    // A rebuild alone does not apply plan changes to jobs, so only a
    // completed sync clears the "sync needed" notice.
    if (completed && mode === "sync") setNeedsSync(false);
    if (message) loadPeriodConsistency();
  };

  const handleSelectRoom = (
    room,
    fromRouteId,
    event,
    roomIndex,
    orderedRooms,
  ) => {
    const selectionEntry = toSelectionEntry(room, fromRouteId);
    const selectionKey = selectionEntry.selectionKey;
    const isCtrlLike = Boolean(event?.ctrlKey || event?.metaKey);
    const isShift = Boolean(event?.shiftKey);

    if (isShift) {
      const normalizedRooms = (orderedRooms || []).map((r) =>
        toSelectionEntry(r, fromRouteId),
      );
      const clickedIndex =
        roomIndex >= 0
          ? roomIndex
          : normalizedRooms.findIndex((r) => r.selectionKey === selectionKey);
      const anchorKey = selectionAnchorByColumn[fromRouteId];
      const anchorIndex = anchorKey
        ? normalizedRooms.findIndex((r) => r.selectionKey === anchorKey)
        : clickedIndex;

      if (clickedIndex >= 0 && anchorIndex >= 0) {
        const start = Math.min(anchorIndex, clickedIndex);
        const end = Math.max(anchorIndex, clickedIndex);
        const range = normalizedRooms.slice(start, end + 1);

        setSelectedRooms((prev) => {
          if (isCtrlLike) {
            const merged = [...prev];
            range.forEach((item) => {
              if (!merged.some((r) => r.selectionKey === item.selectionKey)) {
                merged.push(item);
              }
            });
            return merged;
          }

          const withoutColumn = prev.filter(
            (r) => r.fromRouteId !== fromRouteId,
          );
          return [...withoutColumn, ...range];
        });
      }

      setSelectionAnchorByColumn((prev) => ({
        ...prev,
        [fromRouteId]: selectionKey,
      }));
      return;
    }

    if (isCtrlLike) {
      setSelectedRooms((prev) => {
        const exists = prev.some((r) => r.selectionKey === selectionKey);
        if (exists) {
          return prev.filter((r) => r.selectionKey !== selectionKey);
        }
        return [...prev, selectionEntry];
      });
      setSelectionAnchorByColumn((prev) => ({
        ...prev,
        [fromRouteId]: selectionKey,
      }));
      return;
    }

    setSelectedRooms([selectionEntry]);
    setSelectionAnchorByColumn((prev) => ({
      ...prev,
      [fromRouteId]: selectionKey,
    }));
  };

  const handleMoveRoom = async (targetRouteId) => {
    if (selectedRooms.length === 0) return;
    setSaving(true);
    setSaveError(null);

    try {
      const routes = template.routes || [];
      const routeMap = new Map(
        routes.map((route) => [
          route.pk_id,
          sortRoomsByExecutionOrder(route.rooms || []),
        ]),
      );
      const touchedRouteIds = new Set();

      selectedRooms.forEach((selectedRoom) => {
        const sourceRouteId = selectedRoom.fromRouteId;
        const movingRoomId = selectedRoom.room_id;

        if (sourceRouteId !== "unassigned") {
          const sourceRooms = routeMap.get(sourceRouteId) || [];
          const filtered = sourceRooms.filter((r) => {
            const mappedRoomId = r.room_id || r.pk_id;
            return mappedRoomId !== movingRoomId;
          });
          routeMap.set(sourceRouteId, filtered);
          touchedRouteIds.add(sourceRouteId);
        }

        if (targetRouteId !== "unassigned" && sourceRouteId !== targetRouteId) {
          const targetRooms = routeMap.get(targetRouteId) || [];
          const duplicateExists = targetRooms.some((r) => {
            const existingRoomId = r.room_id || r.pk_id || r.roomId;
            const existingManufacturer = r.manufacturer || null;
            const existingModel = r.device_model || r.deviceModel || null;
            return (
              existingRoomId === movingRoomId &&
              existingManufacturer === (selectedRoom.manufacturer || null) &&
              existingModel === (selectedRoom.device_model || null)
            );
          });

          if (!duplicateExists) {
            targetRooms.push({
              roomId: movingRoomId,
              roomCodeSnapshot:
                selectedRoom.room_code_snapshot || selectedRoom.room_name,
              manufacturer: selectedRoom.manufacturer || null,
              deviceModel: selectedRoom.device_model || null,
              globalOrder: selectedRoom.global_order ?? null,
              active: 1,
            });
          }

          routeMap.set(targetRouteId, targetRooms);
          touchedRouteIds.add(targetRouteId);
        }
      });

      const touchedIds = Array.from(touchedRouteIds);
      const replaceResults = await Promise.all(
        touchedIds.map(async (routeId) => {
          const response = await replaceRouteRooms(
            routeId,
            normalizeRoomsForApi(routeMap.get(routeId) || []),
            DEFER_SYNC,
          );
          return { routeId, rooms: response.rooms || [] };
        }),
      );

      const updatedRoomsByRouteId = new Map(
        replaceResults.map((result) => [result.routeId, result.rooms]),
      );

      setTemplate((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          routes: (prev.routes || []).map((route) =>
            updatedRoomsByRouteId.has(route.pk_id)
              ? { ...route, rooms: updatedRoomsByRouteId.get(route.pk_id) }
              : route,
          ),
        };
      });

      setUnassignedRooms((prev) => {
        let next = [...prev];

        selectedRooms.forEach((selectedRoom) => {
          const sourceRouteId = selectedRoom.fromRouteId;
          const roomId = selectedRoom.room_id || selectedRoom.pk_id;

          if (
            sourceRouteId === "unassigned" &&
            targetRouteId !== "unassigned"
          ) {
            next = next.filter(
              (room) => (room.room_id || room.pk_id) !== roomId,
            );
          }

          if (
            sourceRouteId !== "unassigned" &&
            targetRouteId === "unassigned"
          ) {
            const exists = next.some(
              (room) => (room.room_id || room.pk_id) === roomId,
            );
            if (!exists) {
              next.push({
                pk_id: roomId,
                room_id: roomId,
                room_code_snapshot:
                  selectedRoom.room_code_snapshot || selectedRoom.room_name,
                room_name:
                  selectedRoom.room_name ||
                  selectedRoom.room_code_snapshot ||
                  String(roomId),
                room_restriction: selectedRoom.room_restriction || null,
                department_pk_id: selectedRoom.department_pk_id || null,
                department_name: selectedRoom.department_name || null,
                building_pk_id: selectedRoom.building_pk_id || null,
                building_name: selectedRoom.building_name || null,
                location_pk_id: selectedRoom.location_pk_id || null,
                location_name: selectedRoom.location_name || null,
                manufacturer: selectedRoom.manufacturer || null,
                device_model: selectedRoom.device_model || null,
                active: 1,
              });
            }
          }
        });

        return sortRoomsByExecutionOrder(next);
      });

      setSelectedRooms([]);
      setSelectionAnchorByColumn({});
      markSyncNeeded();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const moveRoomsWithPosition = async (
    roomsToMove,
    targetRouteId,
    targetIndex,
  ) => {
    if (!Array.isArray(roomsToMove) || roomsToMove.length === 0) {
      return;
    }

    setSaving(true);
    setSaveError(null);

    try {
      const routes = template.routes || [];
      const routeMap = new Map(
        routes.map((route) => [
          route.pk_id,
          sortRoomsByExecutionOrder(route.rooms || []),
        ]),
      );
      const touchedRouteIds = new Set();

      const sourceRouteId = roomsToMove[0].fromRouteId;
      const selectedKeys = new Set(roomsToMove.map((r) => r.selectionKey));

      if (sourceRouteId !== "unassigned") {
        const sourceRooms = routeMap.get(sourceRouteId) || [];
        const filtered = sourceRooms.filter((r) => {
          const key = `${sourceRouteId}:${r.pk_id || "na"}:${r.room_id || r.pk_id}`;
          return !selectedKeys.has(key);
        });
        routeMap.set(sourceRouteId, filtered);
        touchedRouteIds.add(sourceRouteId);
      }

      if (targetRouteId !== "unassigned") {
        const targetRooms = [...(routeMap.get(targetRouteId) || [])];
        const targetInsertAt =
          typeof targetIndex === "number"
            ? Math.max(0, Math.min(targetIndex, targetRooms.length))
            : targetRooms.length;

        const movingPayload = roomsToMove.map((selectedRoom) => ({
          roomId: selectedRoom.room_id,
          roomCodeSnapshot:
            selectedRoom.room_code_snapshot || selectedRoom.room_name,
          manufacturer: selectedRoom.manufacturer || null,
          deviceModel: selectedRoom.device_model || null,
          globalOrder: selectedRoom.global_order ?? null,
          active: selectedRoom.active !== undefined ? selectedRoom.active : 1,
        }));

        targetRooms.splice(targetInsertAt, 0, ...movingPayload);
        routeMap.set(targetRouteId, targetRooms);
        touchedRouteIds.add(targetRouteId);
      }

      const touchedIds = Array.from(touchedRouteIds);
      const replaceResults = await Promise.all(
        touchedIds.map(async (routeId) => {
          const response = await replaceRouteRooms(
            routeId,
            normalizeRoomsForApi(routeMap.get(routeId) || []),
            DEFER_SYNC,
          );
          return { routeId, rooms: response.rooms || [] };
        }),
      );

      const updatedRoomsByRouteId = new Map(
        replaceResults.map((result) => [result.routeId, result.rooms]),
      );

      setTemplate((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          routes: (prev.routes || []).map((route) =>
            updatedRoomsByRouteId.has(route.pk_id)
              ? { ...route, rooms: updatedRoomsByRouteId.get(route.pk_id) }
              : route,
          ),
        };
      });

      setUnassignedRooms((prev) => {
        let next = [...prev];

        roomsToMove.forEach((selectedRoom) => {
          const sourceRouteId = selectedRoom.fromRouteId;
          const roomId = selectedRoom.room_id || selectedRoom.pk_id;

          if (
            sourceRouteId === "unassigned" &&
            targetRouteId !== "unassigned"
          ) {
            next = next.filter(
              (room) => (room.room_id || room.pk_id) !== roomId,
            );
          }

          if (
            sourceRouteId !== "unassigned" &&
            targetRouteId === "unassigned"
          ) {
            const exists = next.some(
              (room) => (room.room_id || room.pk_id) === roomId,
            );
            if (!exists) {
              next.push({
                pk_id: roomId,
                room_id: roomId,
                room_code_snapshot:
                  selectedRoom.room_code_snapshot || selectedRoom.room_name,
                room_name:
                  selectedRoom.room_name ||
                  selectedRoom.room_code_snapshot ||
                  String(roomId),
                room_restriction: selectedRoom.room_restriction || null,
                department_pk_id: selectedRoom.department_pk_id || null,
                department_name: selectedRoom.department_name || null,
                building_pk_id: selectedRoom.building_pk_id || null,
                building_name: selectedRoom.building_name || null,
                location_pk_id: selectedRoom.location_pk_id || null,
                location_name: selectedRoom.location_name || null,
                manufacturer: selectedRoom.manufacturer || null,
                device_model: selectedRoom.device_model || null,
                active: 1,
              });
            }
          }
        });

        return sortRoomsByExecutionOrder(next);
      });

      setSelectedRooms([]);
      setSelectionAnchorByColumn({});
      markSyncNeeded();
    } catch (err) {
      setSaveError(err.message || "Failed to move selected rooms");
    } finally {
      setSaving(false);
    }
  };

  const handleDragStartRoom = (
    event,
    room,
    fromRouteId,
    roomIndex,
    orderedRooms,
  ) => {
    if (event?.dataTransfer) {
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData(
        "text/plain",
        String(room.room_id || room.pk_id),
      );
    }

    const sourceRouteId = fromRouteId;
    const clickedEntry = toSelectionEntry(room, sourceRouteId);
    const clickedIsSelected = selectedRooms.some(
      (selected) => selected.selectionKey === clickedEntry.selectionKey,
    );

    let dragSelection = [clickedEntry];

    if (clickedIsSelected) {
      const sameColumnSelected = selectedRooms.filter(
        (selected) => selected.fromRouteId === sourceRouteId,
      );
      if (sameColumnSelected.length > 0) {
        const orderedByColumn = (orderedRooms || []).map((r) =>
          toSelectionEntry(r, sourceRouteId),
        );
        const keys = new Set(sameColumnSelected.map((r) => r.selectionKey));
        dragSelection = orderedByColumn.filter((entry) =>
          keys.has(entry.selectionKey),
        );
      }
    } else {
      setSelectedRooms([clickedEntry]);
    }

    setDragState({
      active: true,
      sourceRouteId,
      overRouteId: sourceRouteId,
      overIndex: roomIndex,
      dragSelectionKeys: dragSelection.map(
        (roomEntry) => roomEntry.selectionKey,
      ),
      dragSelectionEntries: dragSelection,
    });
  };

  const handleDragEndRoom = () => {
    setDragState({
      active: false,
      sourceRouteId: null,
      overRouteId: null,
      overIndex: null,
      dragSelectionKeys: [],
      dragSelectionEntries: [],
    });
  };

  const handleDragOverColumn = (event, routeId, index) => {
    event.preventDefault();
    if (event?.dataTransfer) {
      event.dataTransfer.dropEffect = "move";
    }
    if (!dragState.active || saving) return;

    setDragState((prev) => ({
      ...prev,
      overRouteId: routeId,
      overIndex: index,
    }));
  };

  const handleDropOnColumn = async (event, routeId, index) => {
    event.preventDefault();
    if (!dragState.active || saving) return;

    const draggedRooms = dragState.dragSelectionEntries || [];

    const effectiveTargetIndex = Number.isFinite(index) ? index : null;

    await moveRoomsWithPosition(draggedRooms, routeId, effectiveTargetIndex);

    handleDragEndRoom();
  };

  const handleCreateRoute = async ({ routeCode, routeName }) => {
    setAddRouteSaving(true);
    try {
      const existingOrders = (template?.routes || [])
        .map((route) =>
          Number(route.execution_order || route.executionOrder || 0),
        )
        .filter((value) => Number.isFinite(value) && value > 0);
      const nextExecutionOrder =
        existingOrders.length > 0 ? Math.max(...existingOrders) + 1 : 1;

      const response = await createRoute(
        templateId,
        {
          routeCode,
          routeName,
          executionOrder: nextExecutionOrder,
          active: true,
        },
        DEFER_SYNC,
      );

      const createdRoute = response.route || response;
      if (createdRoute) {
        setTemplate((prev) => {
          if (!prev) return prev;
          const existingRoutes = prev.routes || [];
          const nextExecutionOrder =
            Number(
              createdRoute.execution_order || createdRoute.executionOrder,
            ) || existingRoutes.length + 1;
          return {
            ...prev,
            routes: [
              ...existingRoutes,
              {
                ...createdRoute,
                execution_order: nextExecutionOrder,
                rooms: createdRoute.rooms || [],
              },
            ],
          };
        });
      }

      setAddRouteOpen(false);
      markSyncNeeded();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setAddRouteSaving(false);
    }
  };

  const handleToggleRouteActive = async (route) => {
    if (!route?.pk_id || route.pk_id === "unassigned") return;

    const nextActive = route.active === 0 || route.active === false ? 1 : 0;
    setSaveError(null);
    setRouteStatusSavingById((prev) => ({ ...prev, [route.pk_id]: true }));

    try {
      await updateRoute(route.pk_id, { active: nextActive }, DEFER_SYNC);
      setTemplate((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          routes: (prev.routes || []).map((r) =>
            r.pk_id === route.pk_id ? { ...r, active: nextActive } : r,
          ),
        };
      });
      markSyncNeeded();
    } catch (err) {
      setSaveError(err.message || "Failed to update route status");
    } finally {
      setRouteStatusSavingById((prev) => {
        const next = { ...prev };
        delete next[route.pk_id];
        return next;
      });
    }
  };

  const handleToggleRoomActive = async (routeId, room) => {
    if (!routeId || routeId === "unassigned" || !room?.pk_id) {
      return;
    }

    const roomMappingId = room.pk_id;
    const nextActive = room.active === 0 || room.active === false ? 1 : 0;
    setSaveError(null);
    setRoomStatusSavingById((prev) => ({ ...prev, [roomMappingId]: true }));

    try {
      await updateRouteRoom(roomMappingId, { active: nextActive }, DEFER_SYNC);
      setTemplate((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          routes: (prev.routes || []).map((route) => {
            if (route.pk_id !== routeId) return route;
            return {
              ...route,
              rooms: (route.rooms || []).map((r) =>
                r.pk_id === roomMappingId ? { ...r, active: nextActive } : r,
              ),
            };
          }),
        };
      });
      markSyncNeeded();
    } catch (err) {
      setSaveError(err.message || "Failed to update room status");
    } finally {
      setRoomStatusSavingById((prev) => {
        const next = { ...prev };
        delete next[roomMappingId];
        return next;
      });
    }
  };

  const handleSetSelectedRoomsActive = async (activeValue) => {
    if (selectedRooms.length === 0) return;

    const mappedSelections = selectedRooms.filter(
      (room) => room.fromRouteId !== "unassigned" && room.pk_id,
    );

    if (mappedSelections.length === 0) {
      setSaveError(
        "Only rooms that are assigned to a route can be set active/inactive.",
      );
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      const results = await Promise.allSettled(
        mappedSelections.map((room) =>
          updateRouteRoom(room.pk_id, { active: activeValue }, DEFER_SYNC),
        ),
      );

      const failures = results.filter((r) => r.status === "rejected");
      const updatedRoomIds = new Set(
        results
          .map((result, index) =>
            result.status === "fulfilled"
              ? mappedSelections[index].pk_id
              : null,
          )
          .filter(Boolean),
      );

      if (updatedRoomIds.size > 0) {
        setTemplate((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            routes: (prev.routes || []).map((route) => ({
              ...route,
              rooms: (route.rooms || []).map((room) =>
                updatedRoomIds.has(room.pk_id)
                  ? { ...room, active: activeValue }
                  : room,
              ),
            })),
          };
        });

        setSelectedRooms((prev) =>
          prev.map((room) =>
            updatedRoomIds.has(room.pk_id)
              ? { ...room, active: activeValue }
              : room,
          ),
        );
        markSyncNeeded();
      }

      if (failures.length > 0) {
        setSaveError(
          `${failures.length} af ${mappedSelections.length} rum kunne ikke opdateres.`,
        );
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <>
        <TopNavBar heading="Configure Routes" />
        <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
          <CircularProgress />
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <TopNavBar heading="Configure Routes" />
        <Container maxWidth="lg" sx={{ mt: 3 }}>
          <Alert severity="error">{error}</Alert>
        </Container>
      </>
    );
  }

  const routes = template?.routes || [];
  const unassignedColumn = {
    pk_id: "unassigned",
    route_name: "Ikke tildelt",
    active: 1,
    rooms: unassignedRooms,
  };

  const allColumns = [unassignedColumn, ...routes];

  return (
    <>
      <TopNavBar heading="Configure Routes" />
      <Container maxWidth={false} sx={{ mt: 3, pb: 8, px: { xs: 2, md: 3 } }}>
        {/* Breadcrumb / title */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
          <Tooltip title="Tilbage til liste">
            <IconButton
              size="small"
              onClick={() => navigate("/configure-routes")}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {template?.template_name}
          </Typography>
          <Chip
            label={template?.active ? "Active" : "Inactive"}
            size="small"
            color={template?.active ? "success" : "default"}
            variant="outlined"
          />
          {template?.customer_name && (
            <Typography variant="body2" color="text.secondary">
              · {template.customer_name}
            </Typography>
          )}
        </Box>

        {/* Manufacturer legend */}
        <Box sx={{ display: "flex", gap: 2, mb: 2.5, flexWrap: "wrap" }}>
          {Object.entries(MANUFACTURER_COLORS).map(([mfr, color]) => (
            <Box
              key={mfr}
              sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  bgcolor: color,
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {mfr}
              </Typography>
            </Box>
          ))}
        </Box>

        {saveError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setSaveError(null)}
          >
            {saveError}
          </Alert>
        )}

        {saveSuccess && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSaveSuccess(null)}
          >
            {saveSuccess}
          </Alert>
        )}

        {needsSync && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<SyncIcon />}
                onClick={() => setSyncDialogMode("sync")}
              >
                Sync…
              </Button>
            }
          >
            Route changes are saved but not yet applied to jobs and assignment
            periods. Sync to apply them from today or a day you choose.
          </Alert>
        )}

        {!needsSync &&
          periodConsistency &&
          !periodConsistency.consistent &&
          (() => {
            const s = periodConsistency.summary || {};
            const parts = [
              s.jobs_to_move ? `${s.jobs_to_move} job(s) in the wrong route` : null,
              s.periods_reorder_only
                ? `${s.periods_reorder_only} period(s) out of order`
                : null,
              s.unplaced_jobs ? `${s.unplaced_jobs} job(s) not in a period` : null,
              s.requires_confirmation
                ? `${s.requires_confirmation} manually edited period(s) differ`
                : null,
            ].filter(Boolean);
            return (
              <Alert
                severity="info"
                sx={{ mb: 2 }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => setSyncDialogMode("rebuild")}
                  >
                    Rebuild…
                  </Button>
                }
              >
                Assignment periods from {periodConsistency.from_date} no longer
                match the routes{parts.length ? `: ${parts.join(", ")}` : ""}.
              </Alert>
            );
          })()}

        {/* Kanban columns */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            overflowX: "auto",
            pb: 2,
            alignItems: "flex-start",
          }}
        >
          {allColumns.map((col) => (
            <RouteColumn
              key={col.pk_id}
              route={col}
              isUnassigned={col.pk_id === "unassigned"}
              isRoomSelected={(room) => isRoomSelected(room, col.pk_id)}
              onSelectRoom={(room, event, roomIndex, orderedRooms) =>
                handleSelectRoom(
                  room,
                  col.pk_id,
                  event,
                  roomIndex,
                  orderedRooms,
                )
              }
              onToggleRouteActive={handleToggleRouteActive}
              onToggleRoomActive={handleToggleRoomActive}
              dragState={dragState}
              onDragStartRoom={handleDragStartRoom}
              onDragEndRoom={handleDragEndRoom}
              onDragOverColumn={handleDragOverColumn}
              onDropOnColumn={handleDropOnColumn}
              routeSaving={Boolean(routeStatusSavingById[col.pk_id])}
              roomSavingById={roomStatusSavingById}
            />
          ))}

          {/* Add route button */}
          <Box
            sx={{
              flexShrink: 0,
              alignSelf: "flex-start",
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Button
              startIcon={<AddIcon />}
              variant="outlined"
              size="small"
              onClick={() => setAddRouteOpen(true)}
              sx={{ whiteSpace: "nowrap" }}
            >
              Add Route
            </Button>
            <Button
              startIcon={<SwapVertIcon />}
              variant="outlined"
              size="small"
              onClick={() => setMasterOrderOpen(true)}
              sx={{ whiteSpace: "nowrap" }}
            >
              Master rækkefølge
            </Button>
            <Button
              startIcon={<SyncIcon />}
              variant={needsSync ? "contained" : "outlined"}
              color={needsSync ? "warning" : "primary"}
              size="small"
              onClick={() => setSyncDialogMode("sync")}
              sx={{ whiteSpace: "nowrap" }}
            >
              Sync jobs…
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setSyncDialogMode("rebuild")}
              sx={{ whiteSpace: "nowrap" }}
            >
              Rebuild periods…
            </Button>
          </Box>
        </Box>
      </Container>

      {/* Sticky action bar — appears when a room is selected */}
      {selectedRooms.length > 0 && (
        <Paper
          data-selection-actions="true"
          elevation={4}
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1300,
            px: { xs: 2, md: 4 },
            py: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <CheckCircleOutlineIcon color="primary" fontSize="small" />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {selectedRooms.length} rum valgt
          </Typography>
          <Button
            size="small"
            variant="outlined"
            disabled={saving}
            onClick={() => handleSetSelectedRoomsActive(1)}
          >
            Set active
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={saving}
            onClick={() => handleSetSelectedRoomsActive(0)}
          >
            Set Inactive
          </Button>
          <Typography variant="body2" color="text.secondary">
            Move to:
          </Typography>

          {/* Unassigned target */}
          {selectedRooms.some((room) => room.fromRouteId !== "unassigned") && (
            <Button
              key="unassigned"
              size="small"
              variant="outlined"
              disabled={saving}
              onClick={() => handleMoveRoom("unassigned")}
            >
              Unassigned
            </Button>
          )}

          {/* Route targets */}
          {routes.map((r) => (
            <Button
              key={r.pk_id}
              size="small"
              variant="outlined"
              disabled={saving}
              onClick={() => handleMoveRoom(r.pk_id)}
            >
              {r.route_name || r.route_code}
            </Button>
          ))}

          <Box sx={{ ml: "auto" }}>
            <Button
              size="small"
              color="inherit"
              onClick={() => {
                setSelectedRooms([]);
                setSelectionAnchorByColumn({});
              }}
              disabled={saving}
            >
              Cancel
            </Button>
          </Box>
          {saving && <CircularProgress size={18} />}
        </Paper>
      )}

      <RoutePeriodSyncDialog
        open={syncDialogMode !== null}
        mode={syncDialogMode || "sync"}
        templateId={templateId}
        onClose={handleSyncDialogClose}
      />

      <AddRouteDialog
        open={addRouteOpen}
        onClose={() => setAddRouteOpen(false)}
        onSubmit={handleCreateRoute}
        saving={addRouteSaving}
      />

      <MasterOrderDialog
        open={masterOrderOpen}
        templateId={templateId}
        onClose={(saved) => {
          setMasterOrderOpen(false);
          if (saved) {
            // Master order decides the order of periods that hold several
            // routes (merged periods), so a sync/rebuild is needed.
            markSyncNeeded();
            setSaveSuccess(
              "Master rækkefølge gemt. Kør sync for at opdatere sammenlagte ruter.",
            );
          }
        }}
      />
    </>
  );
}
